import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, PackageCheck, ClipboardCheck, Trash2, Receipt, AlertTriangle,
  CalendarClock, ShieldCheck, ArrowRight, Clock, ShoppingCart, Sparkles, Moon,
} from "lucide-react";
import api from "../lib/api";
import { Card, PageLoader, Badge, Button } from "../lib/ui";
import { fmtNum, fmtUnit } from "../lib/constants";
import { ProductAvatar } from "../components/visual";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../lib/toast";
import { useT } from "../lib/i18n";
import EmployeeMealWidget from "../components/EmployeeMealWidget";

const MASCOT = `${process.env.PUBLIC_URL || ""}/brand/mascot-chef.png`;
const HEALTH_TONE = { EXCELLENT: "success", GOOD: "info", WARNING: "warn", CRITICAL: "danger" };

function LiveClock({ lang }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(id); }, []);
  const locale = lang === "en" ? "en-US" : "id-ID";
  const date = now.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const time = now.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  return (
    <div className="flex flex-wrap items-center gap-2 text-white/95" data-testid="branch-clock">
      <span className="inline-flex items-center gap-1.5 text-sm font-semibold"><Clock size={15} /> {date}</span>
      <span className="rounded-lg bg-white/25 px-2 py-0.5 font-mono text-sm font-extrabold tabular-nums">{time}</span>
    </div>
  );
}

function TaskCard({ icon: Icon, label, value, tone, onClick, testid, pulse }) {
  const tones = {
    info: "text-blue-700",
    primary: "text-primary",
    warn: "text-amber-700",
    danger: "text-red-600",
  };
  return (
    <button onClick={onClick} data-testid={testid}
      className="relative flex flex-col gap-2 rounded-2xl border border-line bg-white p-4 text-left shadow-card transition-all hover:-translate-y-1 hover:shadow-cardhover active:scale-95">
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl bg-app ${tones[tone]}`}><Icon size={20} /></div>
      <p className="font-heading text-3xl font-extrabold leading-none text-ink">{value}</p>
      <p className="text-xs font-semibold leading-tight text-ink-soft">{label}</p>
      {pulse && value > 0 && <span className="absolute right-3 top-3 h-2.5 w-2.5 animate-pulse rounded-full bg-danger" />}
    </button>
  );
}

function BigAction({ icon: Icon, label, color, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-line bg-white p-3 text-center shadow-card transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-cardhover active:scale-95">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: color }}><Icon size={24} /></div>
      <span className="text-[11px] font-bold leading-tight text-ink sm:text-xs">{label}</span>
    </button>
  );
}

function HealthBig({ label, value, tone, testid }) {
  const map = {
    success: { bg: "bg-emerald-50", ring: "ring-emerald-100", dot: "bg-emerald-500", text: "text-emerald-700" },
    warn: { bg: "bg-amber-50", ring: "ring-amber-100", dot: "bg-amber-500", text: "text-amber-700" },
    danger: { bg: "bg-red-50", ring: "ring-red-100", dot: "bg-red-500", text: "text-red-600" },
  }[tone];
  return (
    <div className={`rounded-2xl ${map.bg} p-4 text-center ring-1 ${map.ring}`} data-testid={testid}>
      <span className={`mx-auto mb-2 block h-3 w-3 rounded-full ${map.dot}`} />
      <p className={`font-heading text-3xl font-extrabold leading-none ${map.text}`}>{value}</p>
      <p className="mt-1 text-[11px] font-bold uppercase tracking-wide text-ink-soft">{label}</p>
    </div>
  );
}

export default function BranchDashboard() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [inv, setInv] = useState(null);

  useEffect(() => {
    api.get("/branch/dashboard").then(({ data }) => setData(data)).catch(() => setData({ error: true }));
    api.get("/branch/my-inventory").then(({ data }) => setInv(data)).catch(() => setInv([]));
  }, []);

  const invCounts = useMemo(() => {
    const x = { safe: 0, low: 0, critical: 0 };
    (inv || []).forEach((r) => {
      if (r.status === "CRITICAL") x.critical++;
      else if (r.status === "LOW") x.low++;
      else x.safe++;
    });
    return x;
  }, [inv]);

  if (!data) return <PageLoader />;
  if (data.error) return <Card className="p-8 text-center text-ink-mute">{t("Unable to load your branch dashboard.")}</Card>;

  const c = data.counts || {};
  const h = data.health || {};
  const branchName = data.branch?.name || user?.branch?.name || t("My Branch");
  const lowList = [...(data.critical_items || []), ...(data.low_items || [])];
  const overdue = data.stock_take_due?.overdue;

  const addToOrder = (item, rec) => {
    const qty = rec > 0 ? rec : 1;
    const existing = JSON.parse(localStorage.getItem("cf_order_prefill") || "[]");
    const map = {};
    existing.forEach((e) => { map[e.product_id] = e.qty; });
    map[item.product_id] = qty;
    localStorage.setItem("cf_order_prefill", JSON.stringify(Object.entries(map).map(([product_id, q]) => ({ product_id, qty: Number(q) }))));
    toast.success(`${item.product_name} · ${t("Added to order")}`);
    navigate("/orders/new");
  };

  return (
    <div className="animate-fade-up pb-24 lg:pb-2">
      {/* Welcome hero */}
      <div className="relative mb-5 overflow-hidden rounded-3xl bg-gradient-to-br from-primary to-orange-500 p-5 text-white shadow-pop" data-testid="branch-welcome-hero">
        <div className="relative z-10 max-w-[70%]">
          <p className="text-sm font-bold text-white/90">{t("Hi, Welcome")}</p>
          <h1 className="font-heading text-2xl font-extrabold leading-tight sm:text-3xl" data-testid="branch-name">{branchName}</h1>
          <div className="mt-3"><LiveClock lang={lang} /></div>
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-sm font-bold backdrop-blur" data-testid="branch-health-pill">
            <ShieldCheck size={15} /> {t("Health Score")}: {h.score ?? "—"} · {h.label ? t(h.label) : "—"}
          </div>
        </div>
        <img src={MASCOT} alt="" className="pointer-events-none absolute -bottom-3 right-0 h-32 w-32 object-contain drop-shadow-lg sm:h-44 sm:w-44" />
      </div>

      {/* Night Ops — the Head Chef's guided evening workflow */}
      <button onClick={() => navigate("/night-ops")} data-testid="night-ops-cta"
        className="mb-5 flex w-full items-center gap-3 rounded-3xl bg-gradient-to-br from-indigo-900 via-violet-900 to-slate-900 p-4 text-left text-white shadow-card transition hover:shadow-pop">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15"><Moon size={24} /></span>
        <div className="min-w-0 flex-1">
          <p className="font-heading text-lg font-extrabold leading-tight">{t("Tonight's Tasks")}</p>
          <p className="text-sm text-white/70">{t("Stock take, waste, staff meals & tomorrow's order — one guided checklist")}</p>
        </div>
        <ArrowRight size={20} className="shrink-0 text-white/80" />
      </button>

      {/* Stock take overdue banner */}
      {overdue && (
        <button onClick={() => navigate("/opname")} data-testid="stock-take-due-banner"
          className="mb-5 flex w-full items-center gap-3 rounded-2xl border border-danger/40 bg-danger-light/60 px-4 py-3 text-left transition-all hover:shadow-card">
          <CalendarClock size={22} className="shrink-0 text-danger" />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-danger-text">{t("Stock take overdue")}</p>
            <p className="text-xs text-ink-soft">
              {data.stock_take_due.last_stock_take ? `${t("Last counted")} ${data.stock_take_due.days_since} ${t("day(s) ago")}` : t("No stock take recorded yet")} · {t("target every")} {data.stock_take_due.interval_days} {t("days")}
            </p>
          </div>
          <span className="shrink-0 rounded-xl bg-danger px-3 py-1.5 text-sm font-bold text-white">{t("Count now")}</span>
        </button>
      )}

      {/* Today's tasks */}
      <h2 className="mb-2.5 font-heading text-base font-extrabold text-ink">{t("Today's Tasks")}</h2>
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="branch-today-tasks">
        <TaskCard icon={PackageCheck} label={t("Goods Arriving Today")} value={c.pending_receiving || 0} tone="info" onClick={() => navigate("/receiving")} testid="task-receiving" pulse />
        <TaskCard icon={ClipboardCheck} label={t("Stock Take Today")} value={c.pending_stock_take || 0} tone="primary" onClick={() => navigate("/opname")} testid="task-stocktake" />
        <TaskCard icon={Trash2} label={t("Damaged Goods Today")} value={c.pending_waste || 0} tone="warn" onClick={() => navigate("/waste")} testid="task-waste" />
        <TaskCard icon={AlertTriangle} label={t("Urgent Orders")} value={c.urgent_orders || 0} tone="danger" onClick={() => navigate("/orders")} testid="task-urgent" pulse />
      </div>

      {/* Quick actions */}
      <h2 className="mb-2.5 font-heading text-base font-extrabold text-ink">{t("Quick Actions")}</h2>
      <div className="mb-6 grid grid-cols-3 gap-3 sm:grid-cols-5" data-testid="branch-quick-actions">
        <BigAction icon={Plus} label={t("Create Order")} color="#FF7A00" onClick={() => navigate("/orders/new")} testid="qa-create-order" />
        <BigAction icon={PackageCheck} label={t("Receive Goods")} color="#2563EB" onClick={() => navigate("/receiving")} testid="qa-receive-goods" />
        <BigAction icon={ClipboardCheck} label={t("Start Stock Take")} color="#16A34A" onClick={() => navigate("/opname")} testid="qa-start-stocktake" />
        <BigAction icon={Trash2} label={t("Record Waste")} color="#DC2626" onClick={() => navigate("/waste")} testid="qa-record-waste" />
        <BigAction icon={Receipt} label={t("Input Sales")} color="#D97706" onClick={() => navigate("/sales")} testid="qa-input-sales" />
      </div>

      {/* Inventory health */}
      <h2 className="mb-2.5 font-heading text-base font-extrabold text-ink">{t("Inventory Health")}</h2>
      <div className="mb-6 grid grid-cols-3 gap-3" data-testid="branch-inventory-health">
        <HealthBig label={t("Safe")} value={invCounts.safe} tone="success" testid="health-safe" />
        <HealthBig label={t("Low")} value={invCounts.low} tone="warn" testid="health-low" />
        <HealthBig label={t("Critical")} value={invCounts.critical} tone="danger" testid="health-critical" />
      </div>

      {/* Low stock — order now (most important) */}
      <Card className="mb-6 overflow-hidden p-0" data-testid="branch-low-stock">
        <div className="flex items-center gap-2 border-b border-line bg-orange-50/60 px-4 py-3.5">
          <ShoppingCart size={18} className="text-primary" />
          <h3 className="font-heading text-base font-extrabold text-ink">{t("Running Low — Order Now")}</h3>
          {lowList.length > 0 && <Badge tone="warn" className="ml-1">{lowList.length}</Badge>}
          <button onClick={() => navigate("/my-inventory")} className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-primary">{t("View all")} <ArrowRight size={13} /></button>
        </div>
        {lowList.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-5 py-8 text-center" data-testid="low-stock-empty">
            <Sparkles size={28} className="text-emerald-500" />
            <p className="font-bold text-ink">{t("Everything looks great!")}</p>
            <p className="text-sm text-ink-mute">{t("No items need ordering right now.")}</p>
          </div>
        ) : lowList.slice(0, 8).map((item) => {
          const rec = Math.max(0, (item.ideal_stock || 0) - (item.current_stock || 0));
          return (
            <div key={`${item.product_id}`} className="flex items-center gap-3 border-b border-line/60 px-4 py-3 last:border-0" data-testid={`low-row-${item.product_name}`}>
              <ProductAvatar image={item.image} name={item.product_name} size="md" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold text-ink">{item.product_name}</p>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-ink-soft">
                  <span>{t("Current")}: <b className={item.status === "CRITICAL" ? "text-danger-text" : "text-warn-text"}>{fmtNum(item.current_stock)}</b></span>
                  <span>· {t("Ideal")}: {fmtNum(item.ideal_stock)} {fmtUnit(item.unit)}</span>
                </div>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-[10px] font-bold uppercase text-ink-mute">{t("Recommended")}</p>
                <p className="font-heading text-base font-extrabold text-primary">+{fmtNum(rec)}</p>
              </div>
              <Button size="sm" onClick={() => addToOrder(item, rec)} data-testid={`add-order-${item.product_name}`}><Plus size={15} /> {t("Order")}</Button>
            </div>
          );
        })}
      </Card>

      {/* Arriving + Expiring */}
      <div className="mb-4"><EmployeeMealWidget /></div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="overflow-hidden p-0" data-testid="branch-arriving">
          <div className="flex items-center gap-2 border-b border-line px-4 py-3.5">
            <PackageCheck size={18} className="text-blue-600" />
            <h3 className="font-heading text-base font-extrabold text-ink">{t("Goods Arriving Today")}</h3>
            <button onClick={() => navigate("/receiving")} className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-primary">{t("Receive")} <ArrowRight size={13} /></button>
          </div>
          {(data.pending_receiving || []).length === 0 ? (
            <p className="px-5 py-7 text-center text-sm text-ink-mute">{t("Nothing to receive right now.")}</p>
          ) : (data.pending_receiving || []).slice(0, 5).map((o) => (
            <button key={o.id} onClick={() => navigate("/receiving")} className="flex w-full items-center justify-between border-b border-line/60 px-4 py-3 text-left last:border-0 hover:bg-stone-50" data-testid={`arriving-${o.order_no}`}>
              <div>
                <p className="font-bold text-ink">{o.order_no}</p>
                <p className="text-xs text-ink-mute">{o.items} {t("items")}</p>
              </div>
              <span className="rounded-lg bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700">{t("Receive")}</span>
            </button>
          ))}
        </Card>

        <Card className="overflow-hidden p-0" data-testid="branch-expiring-list">
          <div className="flex items-center gap-2 border-b border-line px-4 py-3.5">
            <CalendarClock size={18} className="text-danger" />
            <h3 className="font-heading text-base font-extrabold text-ink">{t("Expiring Soon")}</h3>
            <button onClick={() => navigate("/expiry")} className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-primary">{t("View all")} <ArrowRight size={13} /></button>
          </div>
          {(data.expiring_items || []).length === 0 ? (
            <p className="px-5 py-7 text-center text-sm text-ink-mute">{t("Nothing expiring soon.")}</p>
          ) : (data.expiring_items || []).slice(0, 5).map((e, idx) => (
            <div key={idx} className="flex items-center justify-between border-b border-line/60 px-4 py-3 last:border-0" data-testid={`expiring-${idx}`}>
              <span className="truncate font-semibold text-ink">{e.product_name}</span>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-xs text-ink-soft">{e.expiry_date}</span>
                <Badge tone={e.expiry_state === "EXPIRED" ? "danger" : "warn"}>{e.expiry_state === "EXPIRED" ? t("Expired") : "≤7d"}</Badge>
              </div>
            </div>
          ))}
        </Card>
      </div>

      <p className="mt-8 flex items-center justify-center gap-1.5 text-xs text-ink-mute">
        <ShieldCheck size={13} /> {t("Scoped to")} {branchName} {t("only")}
      </p>
    </div>
  );
}
